import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CheckCircle2, Fuel, Users, Gauge, Wrench, Zap } from "lucide-react";
import { VEHICLES } from "@/lib/data";
import { Button } from "@/components/ui/button";
import EnquiryDialog from "@/components/EnquiryDialog";

export default function VehicleDetail() {
  const { slug } = useParams();
  const vehicle = VEHICLES.find((v) => v.slug === slug);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  if (!vehicle) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6" data-testid="vehicle-not-found">
        <h1 className="font-display text-4xl font-bold text-white mb-4">Vehicle not found</h1>
        <Link to="/vehicles">
          <Button className="bg-red-600 hover:bg-red-700 rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Vehicles
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`vehicle-detail-${slug}`}>
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[700px] h-[600px] radial-glow-red pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/vehicles"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8 transition-colors"
            data-testid="vehicle-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to vehicles
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden bg-slate-900 aspect-[4/3]">
                <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover" />
              </div>
              <span className="absolute top-4 left-4 bg-yellow-400 text-black text-xs font-bold tracking-wider px-3 py-1.5 rounded font-mono-brand">
                T-PERMIT
              </span>
              {vehicle.isEV && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-cyan-500/20 backdrop-blur-md border border-cyan-400/40 text-cyan-300 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                  <Zap className="w-3 h-3" /> Electric
                </span>
              )}
            </motion.div>

            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-red-500 font-mono-brand mb-3">
                Commercial Vehicle
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-tight">
                {vehicle.name}
              </h1>
              <p className="mt-4 text-xl text-red-400">{vehicle.tagline}</p>

              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
                <Spec icon={Fuel} label="Fuel" value={vehicle.fuel} />
                <Spec icon={Users} label="Seats" value={`${vehicle.seats}`} />
                <Spec icon={Gauge} label="Mileage" value={vehicle.mileage} />
                <Spec icon={Wrench} label="Engine" value={vehicle.engine} />
              </div>

              <div className="mt-8 flex items-center gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400">Starting From</div>
                  <div className="font-display font-black text-4xl text-white">{vehicle.priceFrom}</div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => setEnquiryOpen(true)}
                  size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-full px-8 h-13"
                  data-testid="vehicle-detail-enquire"
                >
                  Enquire Now
                  <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
                <Link to="/finance" data-testid="vehicle-detail-finance">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/15 bg-white/5 text-white hover:bg-white/10 rounded-full"
                  >
                    Check EMI
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-red-500 font-mono-brand mb-3">Highlights</div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-white">
              Why operators choose the {vehicle.name.split(" ")[0]}
            </h2>
            <ul className="mt-8 space-y-4">
              {vehicle.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-red-500 font-mono-brand mb-3">Ideal For</div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-white">
              Use cases
            </h2>
            <div className="mt-8 flex flex-wrap gap-3">
              {vehicle.idealFor.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 rounded-full border border-white/10 bg-white/5 text-sm text-slate-200"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-10 glass rounded-2xl p-6">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-mono-brand mb-1">
                Sample EMI (indicative)
              </div>
              <div className="font-display font-black text-3xl text-white">
                ₹ 16,500 <span className="text-sm text-slate-400 font-normal">/ month</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                60-month tenure, ₹1.5L down-payment, 11% p.a. Actual EMI depends on your profile.
              </p>
              <Link to="/finance">
                <Button variant="outline" className="mt-4 border-white/15 bg-white/5 text-white hover:bg-white/10 rounded-full">
                  Calculate my EMI <ArrowUpRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <EnquiryDialog open={enquiryOpen} onOpenChange={setEnquiryOpen} defaultVehicle={vehicle.name} />
    </div>
  );
}

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3">
      <div className="flex items-center gap-1.5 text-slate-400 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-sm font-semibold text-white">{value}</div>
    </div>
  );
}
